import { useCallback, useContext, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import analyticsService, { ANALYTICS_EVENTS, MODULE_TYPES } from '../utils/analytics';
import { GlobalStateContext } from '../context/GlobalStateContext';
import { useAuth } from './useAuth';

/**
 * React hook for using analytics within components
 * 
 * Benefits:
 * - Provides access to analytics from any component
 * - Automatically includes current screen/route information
 * - Integrates with global state to include user context
 * - Memoizes functions to prevent unnecessary re-renders
 * - Uses centralized useAuth hook to avoid redundant token refreshes
 */
export const useAnalytics = () => {
  const location = useLocation();
  const { user } = useContext(GlobalStateContext) || {};
  
  // Get auth state from centralized useAuth hook (no token refresh needed!)
  const { user: authUser, claims: userClaims } = useAuth();
  const currentUserId = authUser?.uid || null;
  
  // Get current screen for context enrichment
  const currentScreen = useMemo(() => {
    return location.pathname;
  }, [location.pathname]);
  
  // Get user details with proper fallbacks
  const getUserDetails = useCallback(() => {
    return {
      // accountType should be one of Service User or Provider
      accountType: userClaims?.accountType || 
                  (user?.accountType) || 
                  undefined,
                  
      // userType should be one of Org. Admin, Clinician, Support, Patient or Informant
      userType: userClaims?.userType || 
               (user?.userType) || 
               (user?.role) || 
               undefined,
               
      // Organization
      organisation: userClaims?.organisation || 
                   (user?.organisation) || 
                   undefined
    };
  }, [user, userClaims]);
  
  // The screen and user this hook would stamp on an event, held in a ref so
  // reading them does not have to be a dependency of trackEvent.
  //
  // trackEvent used to depend on them directly, which made its identity change
  // as a page settled - and any effect that lists trackEvent in its dependency
  // array then re-runs and emits a SECOND time. Two pages do exactly that, and
  // it is why 48% of assessment_overview_viewed rows on staging (37% in
  // production) are the same arrival announced twice, milliseconds apart, with a
  // byte-identical payload. Report paragraph approvals doubled the same way on
  // 8% of rows, and those feed the adoption figures.
  //
  // The ref is written on every render, so an event still carries the CURRENT
  // screen and user; only the function's identity is stable.
  const contextRef = useRef({});
  contextRef.current = { currentScreen, currentUserId, getUserDetails };

  // Track any event with screen context automatically added
  const trackEvent = useCallback((eventName, params = {}) => {
    const { currentScreen: screen, currentUserId: userId, getUserDetails: read } =
      contextRef.current;
    const userDetails = read();

    analyticsService.trackEvent(eventName, {
      screen,
      userId,
      accountType: userDetails.accountType,
      userType: userDetails.userType,
      organisation: userDetails.organisation,
      ...params,
    });
  }, []);
  
  // Specialized methods for tracking assessment module events
  const trackAssessmentModuleCardClicked = useCallback((moduleType, patientId, additionalParams = {}) => {
    const { currentScreen: screen, currentUserId: userId, getUserDetails: read } =
      contextRef.current;
    const userDetails = read();

    analyticsService.trackAssessmentModuleCardClicked(
      moduleType,
      patientId,
      userId,
      {
        screen,
        accountType: userDetails.accountType,
        userType: userDetails.userType,
        organisation: userDetails.organisation,
        ...additionalParams,
      }
    );
  }, []);
  
  // Track when a section is completed
  const trackSectionCompleted = useCallback((moduleType, sectionId, patientId, additionalParams = {}) => {
    const { currentScreen: screen, currentUserId: userId, getUserDetails: read } =
      contextRef.current;
    const userDetails = read();

    analyticsService.trackSectionCompleted(
      moduleType,
      sectionId,
      patientId,
      userId,
      {
        screen,
        accountType: userDetails.accountType,
        userType: userDetails.userType,
        organisation: userDetails.organisation,
        ...additionalParams,
      }
    );
  }, []);
  
  // Track when a module is completed
  const trackModuleCompleted = useCallback((moduleType, patientId, timeSpentMs, additionalParams = {}) => {
    const { currentScreen: screen, currentUserId: userId, getUserDetails: read } =
      contextRef.current;
    const userDetails = read();

    analyticsService.trackAssessmentModuleCompleted(
      moduleType,
      patientId,
      userId,
      timeSpentMs,
      {
        screen,
        accountType: userDetails.accountType,
        userType: userDetails.userType,
        organisation: userDetails.organisation,
        ...additionalParams,
      }
    );
  }, []);
  
  return {
    trackEvent,
    trackAssessmentModuleCardClicked,
    trackSectionCompleted,
    trackModuleCompleted,
    ANALYTICS_EVENTS,
    MODULE_TYPES,
  };
};

export default useAnalytics; 